import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import FlagIcon from '../../components/FlagIcon';
import { useOnboardingFooter } from '../../components/OnboardingFooterContext';
import SearchableSelect from '../../components/SearchableSelect';
import * as configActions from '../../store/actions/config';
import * as userActions from '../../store/actions/user';
import { buildCompanyOnboardingProfilePayload } from '../../utils/companyOnboardingStorage';
import { normalizeMultiValue } from '../../utils/financialOnboardingStorage';
import { getResolvedNextStepPath, getResolvedPreviousStepPath, isKycOrLaterOnboardingStep } from '../../utils/onboardingUtils';
import normalizeReferenceCountries from '../../utils/referenceCountries';
import { toPascalCaseEnumValue } from '../../utils/companyDocumentCoverage';

const CUSTOMER_TYPE_OPTIONS = [
  {
    value: 'Consumers',
    title: 'Consumers',
    description: 'B2C — individuals',
    icon: (
      <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
        <circle cx='12' cy='8' r='4' />
        <path d='M4 20c0-4 3.6-7 8-7s8 3 8 7' />
      </svg>
    ),
  },
  {
    value: 'Businesses',
    title: 'Businesses',
    description: 'B2B — companies',
    icon: (
      <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
        <rect x='2' y='7' width='20' height='14' rx='1' />
        <path d='M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2' />
      </svg>
    ),
  },
  {
    value: 'Both',
    title: 'Both',
    description: 'Consumers & businesses',
    icon: (
      <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
        <circle cx='18' cy='18' r='3' />
        <circle cx='6' cy='6' r='3' />
        <path d='M13 6h3a2 2 0 0 1 2 2v7' />
        <path d='M11 18H8a2 2 0 0 1-2-2V9' />
      </svg>
    ),
  },
];

const COMPANY_SIZE_OPTIONS = [
  { value: 'OnlyMe', label: 'Only me' },
  { value: 'From2To10', label: '2–10 employees' },
  { value: 'From11To50', label: '11–50 employees' },
  { value: 'From51To200', label: '51–200 employees' },
  { value: 'Over200', label: '200+ employees' },
];

const ANNUAL_REVENUE_OPTIONS = [
  { value: 'UnderHundredThousand', label: 'Under 100.000 €' },
  { value: 'HundredThousandToMillion', label: '100.000 € – 1.000.000 €' },
  { value: 'MillionToTenMillion', label: '1.000.000 € – 10.000.000 €' },
  { value: 'TenMillionToFiftyMillion', label: '10.000.000 € – 50.000.000 €' },
  { value: 'FiftyMillionToTwoHundredAndFiftyMillion', label: '50.000.000 € – 250.000.000 €' },
  { value: 'OverTwoHundredAndFiftyMillion', label: 'Over 250.000.000 €' },
];

const MONTHLY_SEND_VOLUME_OPTIONS = [
  { value: 'UnderFiveThousand', label: 'Under 5.000 €' },
  { value: 'FiveThousandToTenThousand', label: '5.000 € – 10.000 €' },
  { value: 'TenThousandToFiftyThousand', label: '10.000 € – 50.000 €' },
  { value: 'FiftyThousandPlus', label: 'Over 50.000 €' },
];

const MONTHLY_PAYMENT_COUNT_OPTIONS = [
  { value: 'From1To10', label: '1–10 payments' },
  { value: 'From11To50', label: '11–50 payments' },
  { value: 'From51To200', label: '51–200 payments' },
  { value: 'Over200', label: '200+ payments' },
];

const HIGHEST_SINGLE_TRANSFER_OPTIONS = [
  { value: 'Under1000', label: 'Under 1.000 €' },
  { value: 'From1000To10000', label: '1.000 € – 10.000 €' },
  { value: 'From10000To50000', label: '10.000 € – 50.000 €' },
  { value: 'Over50000', label: 'Over 50.000 €' },
];

const sectionHeaderClass =
  'mb-3 mt-[22px] flex items-center gap-1.5 border-b border-[#EEF3F8] pb-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#7A9AB0]/70';
const fieldLabelClass = 'mb-2 block text-[12px] font-normal leading-5 text-[#3D5A73]';
const fieldGroupClass = 'mb-5';
const errorClass = 'mt-1.5 text-xs font-medium text-[#C0392B]';
const searchableSelectTriggerClass =
  'flex h-11 w-full items-center gap-2 rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 text-sm text-[#0B1929] outline-none transition focus:border-[#2E6BAD] focus:ring-[3px] focus:ring-[#2E6BAD]/[.12] disabled:cursor-not-allowed disabled:opacity-50';
const radioCardSelectedClass = 'border-[#1E4E80] shadow-[0_0_0_3px_rgba(30,78,128,0.08)]';
const radioCardIdleClass = 'border-[#DDE6EF] hover:border-[#B8C9D9]';

function SectionHeader({ icon, children }) {
  return (
    <div className={sectionHeaderClass}>
      {icon}
      <span>{children}</span>
    </div>
  );
}

SectionHeader.propTypes = {
  icon: PropTypes.node,
  children: PropTypes.node.isRequired,
};

function normalizeStoredText(value) {
  return value == null ? '' : String(value).trim();
}

function normalizeStoredList(value) {
  return normalizeMultiValue(value)
    .map((item) => item.trim())
    .filter(Boolean)
    .sort();
}

function areListsEqual(first, second) {
  if (first.length !== second.length) return false;
  return first.every((value, index) => value === second[index]);
}

function CompanyActivity() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const setFooterConfig = useOnboardingFooter();
  const { user, onboardingStatus, upsertingCompanyOnboardingProfile, upsertCompanyOnboardingProfileError } = useSelector((state) => state.user);
  const referenceCountries = useSelector((state) => state.config?.referenceCountries ?? []);
  const referenceCountriesLoading = useSelector((state) => state.config?.referenceCountriesLoading ?? false);
  const referenceCountriesError = useSelector((state) => state.config?.referenceCountriesError);
  const nextStepPath =
    getResolvedNextStepPath(location.pathname, onboardingStatus?.steps, { userType: user?.userType }) ?? '/compliance';
  const previousStepPath =
    getResolvedPreviousStepPath(location.pathname, onboardingStatus?.steps, { userType: user?.userType }) ?? '/collect-company-person-data';
  const existingProfile = useMemo(
    () => user?.company?.onboardingProfile ?? user?.company?.OnboardingProfile ?? {},
    [user?.company?.OnboardingProfile, user?.company?.onboardingProfile],
  );
  const [customerType, setCustomerType] = useState('');
  const [operatingCountries, setOperatingCountries] = useState([]);
  const [companySize, setCompanySize] = useState('');
  const [annualRevenue, setAnnualRevenue] = useState('');
  const [monthlySendVolume, setMonthlySendVolume] = useState('');
  const [monthlyPaymentCount, setMonthlyPaymentCount] = useState('');
  const [highestSingleTransfer, setHighestSingleTransfer] = useState('');
  const [destinationCountries, setDestinationCountries] = useState([]);
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [pendingBackendProfile, setPendingBackendProfile] = useState(null);
  const [hasSeenBackendSaving, setHasSeenBackendSaving] = useState(false);

  const isKycOrLaterStep = isKycOrLaterOnboardingStep(onboardingStatus?.currentStep);
  const countryOptions = useMemo(() => normalizeReferenceCountries(referenceCountries), [referenceCountries]);

  useEffect(() => {
    if (countryOptions.length === 0 && !referenceCountriesLoading && !referenceCountriesError) {
      dispatch(configActions.getReferenceCountries());
    }
  }, [countryOptions.length, dispatch, referenceCountriesError, referenceCountriesLoading]);

  const availableOperatingCountryOptions = useMemo(
    () => countryOptions.filter((country) => !operatingCountries.includes(country.code)),
    [countryOptions, operatingCountries],
  );
  const availableDestinationCountryOptions = useMemo(
    () => countryOptions.filter((country) => !destinationCountries.includes(country.code)),
    [countryOptions, destinationCountries],
  );

  const selectedOperatingCountryLabels = useMemo(() => {
    const labels = new Map(countryOptions.map((country) => [country.code, country.name]));
    return operatingCountries.map((code) => ({ code, label: labels.get(code) || code }));
  }, [countryOptions, operatingCountries]);

  const selectedDestinationCountryLabels = useMemo(() => {
    const labels = new Map(countryOptions.map((country) => [country.code, country.name]));
    return destinationCountries.map((code) => ({ code, label: labels.get(code) || code }));
  }, [countryOptions, destinationCountries]);

  const operatingCountrySelectOptions = useMemo(
    () =>
      availableOperatingCountryOptions.map((country) => ({
        value: country.code,
        label: country.name,
        searchText: `${country.name} ${country.code}`,
      })),
    [availableOperatingCountryOptions],
  );

  const destinationCountrySelectOptions = useMemo(
    () =>
      availableDestinationCountryOptions.map((country) => ({
        value: country.code,
        label: country.name,
        searchText: `${country.name} ${country.code}`,
      })),
    [availableDestinationCountryOptions],
  );

  const validate = useCallback(() => {
    const nextErrors = {};
    if (!customerType) nextErrors.customerType = 'Select who your customers are.';
    if (operatingCountries.length === 0) nextErrors.operatingCountries = 'Select at least one country.';
    if (!companySize) nextErrors.companySize = 'Select the size of your company.';
    if (!annualRevenue) nextErrors.annualRevenue = 'Select your annual revenue.';
    if (!monthlySendVolume) nextErrors.monthlySendVolume = 'Select how much money you will send each month.';
    if (!monthlyPaymentCount) nextErrors.monthlyPaymentCount = 'Select how many payments you will send each month.';
    if (!highestSingleTransfer) nextErrors.highestSingleTransfer = 'Select your highest single transfer amount.';
    if (destinationCountries.length === 0) nextErrors.destinationCountries = 'Select at least one destination country.';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [
    annualRevenue,
    companySize,
    customerType,
    destinationCountries.length,
    highestSingleTransfer,
    monthlyPaymentCount,
    monthlySendVolume,
    operatingCountries.length,
  ]);

  useEffect(() => {
    const profile = existingProfile;
    setCustomerType(profile.customerType ?? profile.CustomerType ?? '');
    setOperatingCountries(normalizeMultiValue(profile.operatingCountries ?? profile.OperatingCountries).map((code) => code.toUpperCase()));
    setCompanySize(profile.companySize ?? profile.CompanySize ?? '');
    setAnnualRevenue(toPascalCaseEnumValue(profile.annualRevenue ?? profile.AnnualRevenue ?? ''));
    setMonthlySendVolume(toPascalCaseEnumValue(profile.monthlySendVolume ?? profile.MonthlySendVolume ?? ''));
    setMonthlyPaymentCount(profile.monthlyPaymentCount ?? profile.MonthlyPaymentCount ?? '');
    setHighestSingleTransfer(profile.highestSingleTransfer ?? profile.HighestSingleTransfer ?? '');
    setDestinationCountries(normalizeMultiValue(profile.destinationCountries ?? profile.DestinationCountries).map((code) => code.toUpperCase()));
  }, [existingProfile]);

  const isUnchanged = useCallback(() => {
    const profile = existingProfile;
    return (
      normalizeStoredText(profile.customerType ?? profile.CustomerType) === customerType &&
      areListsEqual(
        normalizeStoredList(profile.operatingCountries ?? profile.OperatingCountries).map((code) => code.toUpperCase()),
        [...operatingCountries].sort(),
      ) &&
      normalizeStoredText(profile.companySize ?? profile.CompanySize) === companySize &&
      toPascalCaseEnumValue(profile.annualRevenue ?? profile.AnnualRevenue) === annualRevenue &&
      toPascalCaseEnumValue(profile.monthlySendVolume ?? profile.MonthlySendVolume) === monthlySendVolume &&
      normalizeStoredText(profile.monthlyPaymentCount ?? profile.MonthlyPaymentCount) === monthlyPaymentCount &&
      normalizeStoredText(profile.highestSingleTransfer ?? profile.HighestSingleTransfer) === highestSingleTransfer &&
      areListsEqual(
        normalizeStoredList(profile.destinationCountries ?? profile.DestinationCountries).map((code) => code.toUpperCase()),
        [...destinationCountries].sort(),
      )
    );
  }, [
    annualRevenue,
    companySize,
    customerType,
    destinationCountries,
    existingProfile,
    highestSingleTransfer,
    monthlyPaymentCount,
    monthlySendVolume,
    operatingCountries,
  ]);

  const buildProfile = useCallback(() => {
    const profile = buildCompanyOnboardingProfilePayload({
      ...existingProfile,
      customerType,
      operatingCountries: operatingCountries.join(','),
      companySize,
      annualRevenue,
      monthlySendVolume,
      monthlyPaymentCount,
      highestSingleTransfer,
      destinationCountries: destinationCountries.join(','),
    });
    return {
      ...profile,
      activityFirstScreenCompleted: true,
      activitySecondScreenCompleted: true,
    };
  }, [
    annualRevenue,
    companySize,
    customerType,
    destinationCountries,
    existingProfile,
    highestSingleTransfer,
    monthlyPaymentCount,
    monthlySendVolume,
    operatingCountries,
  ]);

  const handleContinue = useCallback(() => {
    if (isKycOrLaterStep) {
      if (nextStepPath) navigate(nextStepPath);
      return;
    }

    setHasSubmitted(true);
    if (!validate()) return;

    if (isUnchanged()) {
      setErrors({});
      setHasSubmitted(false);
      navigate(nextStepPath);
      return;
    }

    const profile = buildProfile();
    setPendingBackendProfile(profile);
    setHasSeenBackendSaving(false);
    dispatch(userActions.upsertCompanyOnboardingProfile(profile));
  }, [buildProfile, dispatch, isKycOrLaterStep, isUnchanged, navigate, nextStepPath, validate]);

  const handleBack = useCallback(() => {
    if (!previousStepPath) return;
    if (previousStepPath === '/collect-company-person-data') {
      navigate(previousStepPath, { state: { companyPersonsScreen: 'review' } });
      return;
    }
    navigate(previousStepPath);
  }, [navigate, previousStepPath]);

  useEffect(() => {
    setFooterConfig({
      continueLabel: upsertingCompanyOnboardingProfile ? 'Saving...' : 'Continue',
      continueDisabled: upsertingCompanyOnboardingProfile,
      onContinue: handleContinue,
      backDisabled: upsertingCompanyOnboardingProfile || !previousStepPath,
      onBack: handleBack,
      phaseLabel: 'Activity',
    });
  }, [handleBack, handleContinue, previousStepPath, setFooterConfig, upsertingCompanyOnboardingProfile]);

  useEffect(() => () => setFooterConfig({}), [setFooterConfig]);

  useEffect(() => {
    if (pendingBackendProfile && upsertingCompanyOnboardingProfile) {
      setHasSeenBackendSaving(true);
    }
  }, [pendingBackendProfile, upsertingCompanyOnboardingProfile]);

  useEffect(() => {
    if (pendingBackendProfile && hasSeenBackendSaving && !upsertingCompanyOnboardingProfile && !upsertCompanyOnboardingProfileError) {
      setPendingBackendProfile(null);
      setHasSeenBackendSaving(false);
      setHasSubmitted(false);
      navigate(nextStepPath);
    }
  }, [
    hasSeenBackendSaving,
    navigate,
    nextStepPath,
    pendingBackendProfile,
    upsertCompanyOnboardingProfileError,
    upsertingCompanyOnboardingProfile,
  ]);

  const renderRadioCards = (options, value, onChange, { withIcons = false, cols2 = false } = {}) => (
    <div className={`mt-3 grid gap-2 ${cols2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
      {options.map((option) => {
        const isSelected = value === option.value;
        const title = option.title || option.label;
        return (
          <button
            key={option.value}
            type='button'
            onClick={() => onChange(option.value)}
            className={`flex w-full items-start gap-3 rounded-[12px] border bg-white px-4 py-3.5 text-left transition ${
              isSelected ? radioCardSelectedClass : radioCardIdleClass
            }`}
          >
            {withIcons && option.icon ? (
              <span className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
                {option.icon}
              </span>
            ) : null}
            <span className='min-w-0'>
              <span className='block text-sm font-normal text-[#0B1929]'>{title}</span>
              {option.description ? <span className='mt-0.5 block text-[12px] leading-5 text-[#4E6F8A]'>{option.description}</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );

  const renderCountryPicker = ({ id, selectOptions, selectedLabels, onAdd, onRemove, error }) => (
    <>
      <SearchableSelect
        id={id}
        name={id}
        value=''
        onChange={(event) => {
          const code = event.target.value;
          if (code) onAdd(code);
        }}
        placeholder={referenceCountriesLoading ? 'Loading countries...' : 'Search and add countries…'}
        options={selectOptions}
        showCountryFlags
        searchPlaceholder='Search country...'
        menuPlacement='auto'
        menuMinWidth='w-full'
        clearable={false}
        hasError={Boolean(hasSubmitted && error)}
        triggerClassName={searchableSelectTriggerClass}
      />
      {referenceCountriesError && <p className={errorClass}>Could not load countries. Please refresh and try again.</p>}
      {selectedLabels.length > 0 && (
        <div className='mt-2 flex flex-wrap gap-2'>
          {selectedLabels.map((country) => (
            <button
              key={country.code}
              type='button'
              onClick={() => onRemove(country.code)}
              className='inline-flex items-center gap-1.5 rounded-full border border-[#DDE6EF] bg-[#EBF4FB] px-3 py-1.5 text-[13px] font-medium text-[#0B1929]'
            >
              <FlagIcon countryCode={country.code} className='h-3.5 w-[18px] rounded-[2px]' />
              {country.label}
              <span aria-hidden='true' className='ml-0.5 text-[#7A9AB0]'>
                ×
              </span>
            </button>
          ))}
        </div>
      )}
      {hasSubmitted && error && <p className={errorClass}>{error}</p>}
    </>
  );

  return (
    <div className='flex w-full justify-center bg-[#EBF4FB] px-4 pt-7 font-sans text-[#0B1929] sm:min-h-screen sm:px-6'>
      <div className='w-full max-w-[540px]'>
        <div className='rounded-[20px] bg-white px-6 py-8 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:mb-24 sm:px-10 sm:py-9'>
          <fieldset disabled={isKycOrLaterStep} className='block border-0 p-0'>
            <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
              <svg width='26' height='26' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <rect x='1' y='4' width='22' height='16' rx='2' ry='2' />
                <line x1='1' y1='10' x2='23' y2='10' />
              </svg>
            </div>
            <h1 className='mb-2.5 text-center font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>Business Activity.</h1>
            <p className='mt-2 text-center text-sm leading-6 text-[#4E6F8A]'>Tell us about your business and how you plan to use HansePay.</p>

            <SectionHeader
              icon={
                <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                  <rect x='2' y='7' width='20' height='14' rx='1' />
                  <path d='M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2' />
                </svg>
              }
            >
              Business activity
            </SectionHeader>

            <div className={fieldGroupClass}>
              <fieldset className='border-0 p-0'>
                <legend className={fieldLabelClass}>Who are your customers?</legend>
                {renderRadioCards(CUSTOMER_TYPE_OPTIONS, customerType, setCustomerType, { withIcons: true })}
              </fieldset>
              {hasSubmitted && errors.customerType && <p className={errorClass}>{errors.customerType}</p>}
            </div>

            <div className={fieldGroupClass}>
              <label htmlFor='operatingCountries' className={fieldLabelClass}>
                In which countries does your business operate and serve customers?
              </label>
              {renderCountryPicker({
                id: 'operatingCountries',
                selectOptions: operatingCountrySelectOptions,
                selectedLabels: selectedOperatingCountryLabels,
                onAdd: (code) => setOperatingCountries((prev) => (prev.includes(code) ? prev : [...prev, code])),
                onRemove: (code) => setOperatingCountries((prev) => prev.filter((item) => item !== code)),
                error: errors.operatingCountries,
              })}
            </div>

            <div className={fieldGroupClass}>
              <fieldset className='border-0 p-0'>
                <legend className={fieldLabelClass}>What is the size of your company?</legend>
                {renderRadioCards(COMPANY_SIZE_OPTIONS, companySize, setCompanySize, { cols2: true })}
              </fieldset>
              {hasSubmitted && errors.companySize && <p className={errorClass}>{errors.companySize}</p>}
            </div>

            <div className={fieldGroupClass}>
              <fieldset className='border-0 p-0'>
                <legend className={fieldLabelClass}>What is your annual revenue?</legend>
                {renderRadioCards(ANNUAL_REVENUE_OPTIONS, annualRevenue, setAnnualRevenue, { cols2: true })}
              </fieldset>
              {hasSubmitted && errors.annualRevenue && <p className={errorClass}>{errors.annualRevenue}</p>}
            </div>

            <SectionHeader
              icon={
                <svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                  <polyline points='23 6 13.5 15.5 8.5 10.5 1 18' />
                  <polyline points='17 6 23 6 23 12' />
                </svg>
              }
            >
              Payment volumes
            </SectionHeader>

            <div className={fieldGroupClass}>
              <fieldset className='border-0 p-0'>
                <legend className={fieldLabelClass}>How much money will you send each month?</legend>
                {renderRadioCards(MONTHLY_SEND_VOLUME_OPTIONS, monthlySendVolume, setMonthlySendVolume, { cols2: true })}
              </fieldset>
              {hasSubmitted && errors.monthlySendVolume && <p className={errorClass}>{errors.monthlySendVolume}</p>}
            </div>

            <div className={fieldGroupClass}>
              <fieldset className='border-0 p-0'>
                <legend className={fieldLabelClass}>How many payments will you send each month?</legend>
                {renderRadioCards(MONTHLY_PAYMENT_COUNT_OPTIONS, monthlyPaymentCount, setMonthlyPaymentCount, { cols2: true })}
              </fieldset>
              {hasSubmitted && errors.monthlyPaymentCount && <p className={errorClass}>{errors.monthlyPaymentCount}</p>}
            </div>

            <div className={fieldGroupClass}>
              <fieldset className='border-0 p-0'>
                <legend className={fieldLabelClass}>What is your highest single transfer amount?</legend>
                {renderRadioCards(HIGHEST_SINGLE_TRANSFER_OPTIONS, highestSingleTransfer, setHighestSingleTransfer, { cols2: true })}
              </fieldset>
              {hasSubmitted && errors.highestSingleTransfer && <p className={errorClass}>{errors.highestSingleTransfer}</p>}
            </div>

            <div className={fieldGroupClass}>
              <label htmlFor='destinationCountries' className={fieldLabelClass}>
                Which countries will you mainly send payments to?
              </label>
              {renderCountryPicker({
                id: 'destinationCountries',
                selectOptions: destinationCountrySelectOptions,
                selectedLabels: selectedDestinationCountryLabels,
                onAdd: (code) => setDestinationCountries((prev) => (prev.includes(code) ? prev : [...prev, code])),
                onRemove: (code) => setDestinationCountries((prev) => prev.filter((item) => item !== code)),
                error: errors.destinationCountries,
              })}
            </div>

            {upsertCompanyOnboardingProfileError && (
              <div className='mt-5 rounded-[12px] border border-[#F4C7C3] bg-[#FFF7F6] px-4 py-3 text-sm font-medium text-[#A83227]'>
                {upsertCompanyOnboardingProfileError?.message ??
                  upsertCompanyOnboardingProfileError?.detail ??
                  'Could not save your answers. Please try again.'}
              </div>
            )}
          </fieldset>
        </div>
      </div>
    </div>
  );
}

export default CompanyActivity;
